<script>
import { defineComponent } from 'vue';
import moment from 'moment/moment';
import { mapGetters } from 'vuex';
import { executeCustomModuleCall } from '@/services/api/module.api';
import {
  DHME_M_ASSEMBLY_HALL_DASHBOARD,
  DHME_M_STATION_DASHBOARD,
} from '@/modules/modules';
import AntLoading from '@/components/AntLoading.vue';
import CancelModuleDialog from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/DhmeAssemblyHallDashboardCancelModuleDialog.vue';
import LinkTactDialog from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/DhmeAssemblyHallDashboardLinkTactDialog.vue';
import DhmeStationDashboardAssemblyTaskCard from '@/modules/daiwa-house-modular-europe/StationDashboard/DhmeStationDashboardAssemblyTaskCard.vue';
import { LaravelEchoClient } from '@/services/laravel-echo';
import ColorHelper, {
  getBlackOrWhiteBasedOnBackground,
} from '@/services/color-helper';
import DhmeHallDashboardAssemblySubTaskCard from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/DhmeHallDashboardAssemblySubTaskCard.vue';
import FileHandlerService from '@/services/file-handler';
import {
  downloadTaskAppendix,
  queryTasksV2,
  reopenTask,
  updateTask,
} from '@/services/api/task.api';
import { importTasksV2 } from '@/services/api/v2/connect_v2';

// Orders are stored as text; compare them as numbers ("5" before "18")
const compareNatural = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });

export default defineComponent({
  name: 'DhmeAssemblyHallDashboardPlanning',
  components: {
    DhmeHallDashboardAssemblySubTaskCard,
    DhmeStationDashboardAssemblyTaskCard,
    LinkTactDialog,
    CancelModuleDialog,
    AntLoading,
  },
  data: () => {
    return {
      assemblyTasks: [],
      focusedTask: null,
      isLoading: false,
      cancelDialog: false,
      linkTactDialog: false,
      assemblySubTasks: [],
      assemblyLocations: [],
      editModeActive: false,
      filterModulesTab: 'todo',
      taskToMove: null,
      statusProject: null,
      projectConfigurationId: null,
      projectStatus: null,
      projectStatusLoading: false,
      filteredLane: [],
      stepTab: 0,
      searchModule: '',
      previewDialog: false,
      imagePreview: null,
      focusedOutHallTask: null,
      subTasksLoading: false,
      pingDialog: false,
      pingMessage: '',
      recalibrateConfirmDialog: false,
      recalibrateHallLoading: false,
    };
  },
  computed: {
    ColorHelper() {
      return ColorHelper;
    },
    ...mapGetters({
      project: 'project',
      focusedHall: 'dhmeAssemblyHallDashboardStore/focusedHall',
      activeLocations: 'dhmeAssemblyHallDashboardStore/activeHallLocations',
      hallLocations: 'dhmeAssemblyHallDashboardStore/hallLocations',
      projects: 'dhmeAssemblyHallDashboardStore/hallProjects',
      hallLanes: 'dhmeAssemblyHallDashboardStore/hallLanes',
      configurationSteps: 'dhmeAssemblyHallDashboardStore/configurationSteps',
      isProductieManager: 'isProductieManager',
    }),
    hallViewLocations() {
      if (this.statusProject) {
        return this.hallLocations.filter(
          (l) => l.hall_configuration === this.projectConfigurationId
        );
      }
      return this.activeLocations.map((x) => x.location);
    },
    locations() {
      return this.hallViewLocations
        .map((l) => {
          l.lane = this.activeLanes.find((y) =>
            y.laneIds.includes(l.assembly_lane)
          );
          return l;
        })
        .filter((l) =>
          this.filteredLane.length > 0
            ? this.filteredLane.includes(l.lane?.id)
            : true
        )
        .sort(
          (a, b) =>
            this.activeLanes.indexOf(a.lane) -
              this.activeLanes.indexOf(b.lane) ||
            compareNatural(a.order, b.order) ||
            compareNatural(a.assembly_location, b.assembly_location)
        );
    },
    canPlaceFocusedTask() {
      // Only the selected project's own modules, on its own configuration
      return (
        !!this.focusedTask &&
        !this.focusedTask.task_type.custom_11 &&
        !!this.projectConfigurationId &&
        this.focusedTask.project === this.statusProject?.id
      );
    },
    moduleId() {
      return this.project.modules.find(
        (module) => module.route === DHME_M_ASSEMBLY_HALL_DASHBOARD
      ).id;
    },
    stationDashboardModuleId() {
      return this.project.modules.find(
        (module) => module.route === DHME_M_STATION_DASHBOARD
      ).id;
    },
    activeProject() {
      const task = this.assemblyTasks[0];
      if (task) {
        return this.projects?.find((p) => p.id === task.project);
      }
      return null;
    },
    activeProjects() {
      return [
        ...new Map(
          this.assemblyTasks
            .filter((t) => t.task_project)
            .map((t) => t.task_project)
            .map((item) => [item.id, item])
        ).values(),
      ];
    },
    activeLanes() {
      const locations = this.hallViewLocations;
      // Every configuration has its own lane records for the same physical
      // lane, so lanes with the same title are shown as one lane
      const laneKey = (lane) => (lane.title ?? '').trim().toLowerCase();
      const lanes = [];
      this.hallLanes
        .filter((l) => locations.some((x) => x.assembly_lane === l.id))
        .forEach((l) => {
          const lane = lanes.find((x) => laneKey(x) === laneKey(l));
          if (lane) {
            lane.laneIds.push(l.id);
          } else {
            lanes.push({ ...l, laneIds: [l.id] });
          }
        });

      const firstLocationOrder = (lane) =>
        locations
          .filter((l) => lane.laneIds.includes(l.assembly_lane))
          .map((l) => l.order)
          .sort(compareNatural)[0];

      return lanes.sort(
        (a, b) =>
          compareNatural(a.order, b.order) ||
          compareNatural(firstLocationOrder(a), firstLocationOrder(b))
      );
    },
    filteredAssemblyTasks() {
      if (this.searchModule) {
        return this.assemblyTasks.filter((t) => {
          if (
            t.assemblyItem.id.includes(this.searchModule) ||
            t.assemblyItem.code.includes(this.searchModule) ||
            t.assemblyItem.type.includes(this.searchModule)
          ) {
            return true;
          }
        });
      }
      return this.assemblyTasks;
    },
    toDoTasks() {
      return this.filteredAssemblyTasks.filter((t) => {
        if (!['open', 'canceled'].includes(t.status) || t.task_type.custom_9) {
          return false;
        }

        if (t.task_type.custom_11) {
          const sameCustom11Tasks = this.assemblyTasks
            .filter((t) => t.status !== 'closed')
            .filter(
              (task) => task.task_type.custom_11 === t.task_type.custom_11
            );

          const earliestPlannedStart = sameCustom11Tasks.reduce(
            (earliest, currentTask) =>
              moment(currentTask.planned_start).isBefore(
                moment(earliest.planned_start)
              )
                ? currentTask
                : earliest
          ).planned_start;

          if (!moment(t.planned_start).isAfter(moment(earliestPlannedStart))) {
            return false;
          }
        }

        return true;
      });
    },
    outHallTasks() {
      return this.filteredAssemblyTasks
        .filter(
          (t) =>
            (!t.task_type.custom_11 && t.task_type.custom_9) ||
            t.status === 'closed'
        )
        .sort(
          (a, b) =>
            moment(b.planned_start).unix() - moment(a.planned_start).unix()
        );
    },
    tasksOnLocations() {
      return this.filteredAssemblyTasks.filter(
        (t) => ['open', 'canceled'].includes(t.status) && t.task_type.custom_11
      );
    },
    // planned_start values shared by module tasks from more than one project — two projects
    // planned on the same date + time in one hall is a planning error the operator should catch.
    collisionPlannedStarts() {
      const projectsByStart = {};
      this.assemblyTasks
        .filter((t) => t.task_type?.type === 'dhme-module-assembly')
        .forEach((t) => {
          if (!projectsByStart[t.planned_start]) {
            projectsByStart[t.planned_start] = new Set();
          }
          projectsByStart[t.planned_start].add(t.project);
        });
      return Object.keys(projectsByStart).filter(
        (start) => projectsByStart[start].size > 1
      );
    },
    assemblyLocationSteps() {
      let steps = Array.from(
        new Set(this.assemblySubTasks.map((t) => t?.task_type?.custom_6))
      );

      // Check if all elements are UUIDs
      let isUuid = steps.every((step) => {
        const uuidRegex =
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/i;
        return uuidRegex.test(step);
      });

      if (isUuid) {
        return this.configurationSteps
          .filter((s) => steps.includes(s.id))
          .sort((a, b) => parseInt(a.order) - parseInt(b.order));
      } else {
        return steps.sort((a, b) => parseInt(a) - parseInt(b));
      }
    },
    totalProgressAcrossSteps() {
      const closedTasks = this.assemblySubTasks.filter((t) =>
        ['closed', 'canceled'].includes(t.status)
      ).length;

      if (closedTasks > 0) {
        return Math.ceil((closedTasks / this.assemblySubTasks.length) * 100);
      }
      return 0;
    },
  },
  watch: {
    focusedHall(value) {
      if (value) {
        this.fetchModuleAssemblyTasks();
      }
    },
    async focusedTask(value) {
      if (value) {
        this.subTasksLoading = true;
        this.assemblySubTasks = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchAssemblySubTasks',
          {
            parent: value.id,
          }
        );

        this.assemblyLocations = this.hallLocations
          .filter((l) =>
            this.assemblySubTasks.some((t) => t.task_type.custom_2 === l.id)
          )
          .sort((l1, l2) =>
            compareNatural(l1.assembly_location, l2.assembly_location)
          );
        this.subTasksLoading = false;
      }
    },
    async statusProject(value) {
      // The previous project's locations must not stay placeable while switching
      this.projectConfigurationId = null;
      this.focusedTask = null;
      if (value) {
        this.projectStatusLoading = true;
        this.projectStatus = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchProjectStatus',
          {
            project: value.id,
          }
        );
        this.projectStatusLoading = false;

        let taskResponse = await queryTasksV2(value.license, [
          {
            column: 'project',
            operator: '=',
            values: [value.id],
          },
          {
            column: 'type',
            operator: '=',
            values: ['dhme-assembly-phase'],
          },
        ]);
        // A newer selection took over while this one was loading
        if (this.statusProject?.id !== value.id) {
          return;
        }
        if (taskResponse.tasks.length === 1) {
          this.projectConfigurationId =
            taskResponse.tasks[0].task_type.custom_4;
        } else {
          this.$store.commit('showNotification', {
            content: 'Assembly task not found for current project',
            color: 'error',
          });
        }
      } else {
        this.projectStatus = null;
        await this.$store.dispatch('selectAssemblyHall', this.focusedHall);
      }

      await this.fetchModuleAssemblyTasks();
    },
  },
  mounted() {
    this.fetchModuleAssemblyTasks();
    this.$store.dispatch('dhmeAssemblyHallDashboardStore/fetchHallTacts');
  },
  methods: {
    async previewImage(attachment) {
      let file = await downloadTaskAppendix(attachment.task, attachment.id);

      let blob = await FileHandlerService.convertBase64ToBlob(
        file.file,
        file.mimetype
      );

      attachment.url = URL.createObjectURL(blob);

      this.imagePreview = attachment;
      this.previewDialog = true;
    },
    getBlackOrWhiteBasedOnBackground,
    async reinstateOfflineAGV() {
      await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'reinstateOfflineAgv',
        {
          task: this.focusedTask.id,
          hall: this.focusedHall.id,
          stationDashboardModule: this.stationDashboardModuleId,
        }
      );

      await this.fetchModuleAssemblyTasks();
    },
    fetchModuleAssemblyTasks: async function () {
      this.isLoading = true;
      this.focusedTask = null;
      let data = {
        hall: this.focusedHall.id,
      };
      if (this.statusProject) {
        data.project = this.statusProject?.id;
      }
      this.assemblyTasks = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'fetchHallAssemblyTasks',
        data
      );
      this.isLoading = false;
    },
    getTasksByLocation(locationId) {
      return this.assemblySubTasks
        .filter(
          (t) =>
            t.task_type.custom_2 === locationId &&
            (this.assemblyLocationSteps[this.stepTab]?.id ??
              this.assemblyLocationSteps[this.stepTab]) ===
              t.task_type?.custom_6?.toString()
        )
        .sort(
          (a, b) =>
            parseFloat(a.task_type.custom_5) - parseFloat(b.task_type.custom_5)
        );
    },
    async exportCurrentProject() {
      const response = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'createClosedTaskExport',
        { hall: this.selectedAssemblyHall.id, project: this.activeProject.id }
      );
    },
    forceReloadLocation(locationId) {
      LaravelEchoClient.private(
        `App.Project.${this.project.id}.Module.${this.stationDashboardModuleId}.Location.${locationId}`
      ).whisper('reloadData', {});
      this.$store.commit('showNotification', {
        content: 'Pushed force reload to location',
        color: 'info',
      });
    },
    forceReloadLocations() {
      this.assemblyLocations.forEach((l) => {
        LaravelEchoClient.private(
          `App.Project.${this.project.id}.Module.${this.stationDashboardModuleId}.Location.${l.id}`
        ).whisper('reloadData', {});
      });
      this.$store.commit('showNotification', {
        content: 'Pushed data reload to all locations',
        color: 'info',
      });
    },
    hardReloadLocation(locationId) {
      LaravelEchoClient.private(
        `App.Project.${this.project.id}.Module.${this.stationDashboardModuleId}.Location.${locationId}`
      ).whisper('hardReload', {});
      this.$store.commit('showNotification', {
        content: 'Pushed hard reload to location',
        color: 'info',
      });
    },
    hardReloadLocations() {
      this.assemblyLocations.forEach((l) => {
        LaravelEchoClient.private(
          `App.Project.${this.project.id}.Module.${this.stationDashboardModuleId}.Location.${l.id}`
        ).whisper('hardReload', {});
      });
      this.$store.commit('showNotification', {
        content: 'Pushed hard reload to all locations',
        color: 'info',
      });
    },
    async addSpaceAfterThisTask() {
      const task = {
        title: 'Space',
        parent: this.focusedTask.parent,
        project: this.focusedTask.task_project.id,
        planned_start: moment(this.focusedTask.planned_start)
          .add(10, 'second')
          .format('YYYY-MM-DD HH:mm:ss'),
        planned_end: moment(this.focusedTask.planned_end)
          .add(10, 'second')
          .format('YYYY-MM-DD HH:mm:ss'),
        options: {
          type: 'dhme-assembly-space',
          custom_2: this.focusedTask.task_type.custom_2,
          custom_3: this.focusedTask.task_type.custom_3,
        },
      };

      const result = await importTasksV2([task]);

      // Normalize return value (array or single object)
      const importedTasks = Array.isArray(result) ? result : [result];

      this.assemblyTasks = [...this.assemblyTasks, ...importedTasks].sort(
        (a, b) =>
          moment(a.planned_start).unix() - moment(b.planned_start).unix()
      );
    },
    async recalibrateHall() {
      this.recalibrateHallLoading = true;
      try {
        await executeCustomModuleCall(
          this.project.id,
          this.stationDashboardModuleId,
          'recalibrateHall',
          {
            HallId: this.focusedHall.id,
            TaskId: this.focusedTask.id,
          }
        );
        this.$store.commit('showNotification', {
          content: 'Hall recalibrated successfully',
          color: 'success',
        });
        this.recalibrateConfirmDialog = false;
        // Reload the full hall so locations, active locations, positions and
        // tacts reflect the recalibration. selectAssemblyHall refreshes the
        // hall locations/lanes/steps and re-runs fetchActiveLocationIds, while
        // fetchModuleAssemblyTasks reloads the assembly task list.
        await this.$store.dispatch(
          'dhmeAssemblyHallDashboardStore/selectAssemblyHall',
          this.focusedHall
        );
        await this.fetchModuleAssemblyTasks();
        await this.$store.dispatch(
          'dhmeAssemblyHallDashboardStore/fetchHallTacts'
        );
      } catch (err) {
        this.$store.commit('showNotification', {
          content:
            err?.message || 'Something went wrong while recalibrating the hall',
          color: 'error',
        });
      } finally {
        this.recalibrateHallLoading = false;
      }
    },
    reopenTask() {
      reopenTask(this.focusedTask.id);
      this.focusedTask.status = 'open';
    },
    moveTask(newLocation) {
      importTasksV2([
        {
          id: this.taskToMove.id,
          options: {
            custom_2: newLocation.id,
          },
        },
      ]);
      this.taskToMove.task_type.custom_2 = newLocation.id;
      this.taskToMove = null;
    },
    setTaskOnLocation(locationId) {
      if (
        !this.canPlaceFocusedTask ||
        !this.locations.some((l) => l.id === locationId)
      ) {
        return;
      }
      let data = {
        id: this.focusedTask.id,
        options: {
          custom_11: locationId,
        },
      };

      if (!this.focusedHall.automated) {
        data.options.custom_3 = locationId;
      }

      const body = [
        data,
        // ...this.assemblySubTasks.map((s) => {
        //   return {
        //     id: s.id,
        //     options: {
        //       custom_2: locationId,
        //     },
        //   };
        // }),
      ];

      importTasksV2(body);

      if (!this.focusedHall.automated) {
        this.focusedTask.task_type.custom_3 = locationId;
      }
      this.focusedTask.task_type.custom_11 = locationId;
    },
    unlinkTact() {
      let data = {
        id: this.focusedTask.id,
        options: {
          custom_9: null,
        },
      };

      importTasksV2([data]);

      this.focusedTask.task_type.custom_9 = null;
    },
    unlinkLocation() {
      let data = {
        id: this.focusedTask.id,
        options: {
          custom_11: null,
        },
      };

      if (!this.focusedHall.automated) {
        data.options.custom_3 = null;
      }
      importTasksV2([data]);

      if (!this.focusedHall.automated) {
        this.focusedTask.task_type.custom_3 = null;
      }
      this.focusedTask.task_type.custom_11 = null;
    },
    async markAsComplete() {
      try {
        await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'completeAssembly',
          {
            task: this.focusedTask.id,
          }
        );
        this.focusedTask.status = 'closed';
      } catch (e) {
        this.$store.commit('showNotification', {
          content: e.message,
          color: 'error',
        });
      }
    },

    sendManuallyLocationOk(locationId) {
      executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'manuallySendLocationOk',
        {
          location: locationId,
        }
      );
    },
    focusOnTask(task, isOutHall = false) {
      if (task === this.focusedTask) {
        this.focusedTask = null;
        this.focusedOutHallTask = null;
      } else {
        this.focusedTask = task;
        this.focusedOutHallTask = isOutHall ? task : null;
      }
    },
    async requestAssemblyReport() {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'exportProjectAssemblyReport',
        {
          project: this.statusProject.id,
          timezone: timezone,
        }
      );

      this.$store.commit('showNotification', {
        content: response.message,
        color: 'info',
      });
    },
    sendPingToAllLocations() {
      const payload = {
        message: this.pingMessage,
        sentAt: new Date().toISOString(),
      };
      this.locations.forEach((l) => {
        LaravelEchoClient.private(
          `App.Project.${this.project.id}.Module.${this.stationDashboardModuleId}.Location.${l.id}`
        ).whisper('ping', payload);
      });
      this.$store.commit('showNotification', {
        content: `Ping sent to ${this.locations.length} location(s)`,
        color: 'info',
      });
      this.pingMessage = '';
      this.pingDialog = false;
    },
    async openLeanForm() {
      const response = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'generateLeanForm',
        {
          task: this.focusedTask.id,
        }
      );

      if (response?.url) {
        const guidMatch = response.url.match(/FormGuid=([0-9a-fA-F-]+)/);
        if (guidMatch && guidMatch[1]) {
          this.focusedTask.task_type.custom_15 = guidMatch[1];
        }

        window.open(response.url, '_blank');
      }
    },
  },
});
</script>

<template>
  <div class="d-flex flex-column" style="width: 0">
    <v-toolbar dense flat height="50" style="max-height: 50px">
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-icon class="mr-2" v-bind="attrs" v-on="on">
            mdi-information-box-outline
          </v-icon>
        </template>
        <span>In this toolbar you can see the project current status </span>
      </v-tooltip>
      <v-combobox
        v-model="statusProject"
        :items="activeProjects"
        :loading="projectStatusLoading"
        class="mr-5"
        clearable
        dense
        filled
        hide-details
        item-text="name"
        item-value="id"
        placeholder="Project"
        single-line
        style="max-width: 300px"
      >
        <template #item="{ item }">
          {{ item.name }}
          <v-spacer />
          <v-chip small>{{ item.number }}</v-chip>
        </template>
      </v-combobox>
      <v-text-field
        v-model="searchModule"
        clearable
        dense
        filled
        hide-details
        item-text="name"
        item-value="id"
        placeholder="Search module"
        prepend-inner-icon="mdi-magnify"
        single-line
        style="max-width: 200px"
      />
      <v-spacer />
      <div v-if="projectStatus">
        <span v-if="focusedHall.automated">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon class="mx-2" v-bind="attrs" v-on="on"> mdi-cube</v-icon>
            </template>
            <span>Modules through moved through hall</span>
          </v-tooltip>
          {{ projectStatus.modules_through_hall }}/{{
            projectStatus.modules_total
          }}
        </span>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon class="mx-2" v-bind="attrs" v-on="on">
              mdi-checkbox-outline
            </v-icon>
          </template>
          <span>Modules fully completed</span>
        </v-tooltip>
        {{ projectStatus.modules_done }}/{{ projectStatus.modules_total }}

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon class="mx-2" v-bind="attrs" v-on="on">
              mdi-keyboard-space
            </v-icon>
          </template>
          <span>Spaces created in project </span>
        </v-tooltip>
        {{ projectStatus.spaces_created }}
      </div>
      <v-tooltip v-if="statusProject" bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            class="ml-4"
            color="primary"
            elevation="0"
            small
            v-bind="attrs"
            @click="requestAssemblyReport"
            v-on="on"
          >
            <v-icon class="mr-2">mdi-export</v-icon>
            export
          </v-btn>
        </template>
        <span>Export assembly report </span>
      </v-tooltip>
    </v-toolbar>
    <div class="d-flex flex-1 overflow-y-auto">
      <div
        class="d-flex flex-column overflow-y-auto full-height"
        style="width: 20%"
      >
        <v-toolbar dense flat height="50" style="max-height: 50px">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon class="mr-2" v-bind="attrs" v-on="on">
                mdi-information-box-outline
              </v-icon>
            </template>
            <span>Below you will the tasks which are planned for today </span>
          </v-tooltip>
          <span> TO DO ({{ toDoTasks?.length ?? '-' }})</span>
        </v-toolbar>
        <div v-if="!isLoading" class="overflow-y-auto flex-1 my-2 px-2">
          <dhme-station-dashboard-assembly-task-card
            v-for="task in toDoTasks"
            :key="task.id"
            :collision-starts="collisionPlannedStarts"
            :details="true"
            :focused-task="focusedTask"
            :task="task"
            @click="focusOnTask(task)"
          />
        </div>
        <div v-else class="flex-1 d-flex align-center justify-center">
          <ant-loading />
        </div>
      </div>
      <div class="d-flex flex-column flex-1" style="width: 40%">
        <v-toolbar dense flat height="50" style="max-height: 50px">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon class="mr-2" v-bind="attrs" v-on="on">
                mdi-information-box-outline
              </v-icon>
            </template>
            <span
              >Below you will the current situation of the selected hall
            </span>
          </v-tooltip>
          <v-chip class="mr-2" small>{{ focusedHall.title }}</v-chip>
          IN HALL ({{ tasksOnLocations?.length ?? '-' }})
          <v-spacer />
          <v-select
            v-if="activeLanes.length > 1"
            v-model="filteredLane"
            :items="activeLanes"
            class="flex-grow-0"
            clearable
            dense
            filled
            hide-details
            item-text="title"
            item-value="id"
            multiple
            placeholder="Lane"
            prepend-icon="mdi-filter"
            single-line
            style="width: 250px"
          >
            <template #item="{ item }">
              <v-checkbox
                :ripple="false"
                :value="filteredLane.includes(item.id)"
                class="ma-0"
                hide-details
                readonly
              />
              <v-chip
                :color="item.color"
                :text-color="getBlackOrWhiteBasedOnBackground(item.color)"
                class="mr-5"
                small
                >{{ item.title }}
              </v-chip>
            </template>
            <template #selection="{ item, index }">
              <v-chip
                v-if="index === 0"
                :color="item.color"
                :text-color="getBlackOrWhiteBasedOnBackground(item.color)"
                small
              >
                <span>{{ item.title }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text text-caption">
                (+{{ filteredLane.length - 1 }}
                {{ filteredLane.length - 1 > 1 ? 'others' : 'other' }})
              </span>
            </template>
          </v-select>

          <v-menu bottom left>
            <template #activator="{ on, attrs }">
              <v-icon class="ant-icon ml-2" dense v-bind="attrs" v-on="on"
                >mdi-dots-vertical
              </v-icon>
            </template>
            <v-list dense>
              <v-list-item
                v-if="focusedTask"
                :disabled="
                  focusedTask.status !== 'open' ||
                  focusedTask.task_type.type !== 'dhme-module-assembly'
                "
                @click="linkTactDialog = true"
              >
                <v-list-item-icon>
                  <v-icon>mdi-link</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Link tact</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openLeanForm">
                <v-list-item-icon>
                  <v-icon>mdi-clipboard-text</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  >{{
                    focusedTask?.task_type.custom_15 ? 'Open' : 'Generate'
                  }}
                  Form</v-list-item-title
                >
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.task_type.custom_9"
                @click="unlinkTact"
              >
                <v-list-item-icon>
                  <v-icon>mdi-link-off</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Unlink task tact</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.task_type.custom_11"
                @click="unlinkLocation"
              >
                <v-list-item-icon>
                  <v-icon>mdi-link-variant-off</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Remove from location</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.status === 'closed'"
                dense
                @click="reopenTask"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-clipboard-arrow-left</v-icon>
                </v-list-item-icon>
                <v-list-item-title> Reopen Task</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.status === 'open'"
                @click="markAsComplete"
              >
                <v-list-item-icon>
                  <v-icon>mdi-checkbox-marked-outline </v-icon>
                </v-list-item-icon>
                <v-list-item-title>Mark as complete</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask"
                :disabled="
                  focusedTask.status !== 'open' ||
                  focusedTask.task_type.type !== 'dhme-module-assembly'
                "
                @click="cancelDialog = true"
              >
                <v-list-item-icon>
                  <v-icon>mdi-cancel</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Cancel module</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="
                  focusedTask && focusedTask.task_type.custom_7 === 'inactive'
                "
                @click="reinstateOfflineAGV"
              >
                <v-list-item-icon></v-list-item-icon>
                <v-list-item-title>Reinstate offline AGV</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask"
                dense
                @click="forceReloadLocations"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-reload</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Reload data on all locations
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.task_type.custom_11"
                dense
                @click="forceReloadLocation(focusedTask.task_type.custom_11)"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-reload</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Reload data on this location
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask?.task_type.custom_11"
                dense
                @click="hardReloadLocation(focusedTask.task_type.custom_11)"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-reload-alert</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Hard reload this location
                </v-list-item-title>
              </v-list-item>
              <v-list-item dense @click="exportCurrentProject">
                <v-list-item-icon>
                  <v-icon dense> mdi-file-download</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Export {{ activeProject?.name }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask"
                dense
                @click="hardReloadLocations"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-reload-alert</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  >Hard reload all locations
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="focusedTask"
                dense
                @click="addSpaceAfterThisTask"
              >
                <v-list-item-icon>
                  <v-icon dense> mdi-keyboard-space</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  >Add space after this task
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="
                  focusedHall.automated &&
                  focusedTask &&
                  ['dhme-module-assembly', 'dhme-assembly-space'].includes(
                    focusedTask.task_type.type
                  )
                "
                dense
                @click="recalibrateConfirmDialog = true"
              >
                <v-list-item-icon>
                  <v-icon dense>mdi-arrow-collapse-all</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Recalibrate hall</v-list-item-title>
              </v-list-item>
              <v-list-item dense @click="pingDialog = true">
                <v-list-item-icon>
                  <v-icon dense>mdi-access-point</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  >Send test message to all locations</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>

          <cancel-module-dialog
            v-if="focusedTask"
            :displayed="cancelDialog"
            :module-task="focusedTask"
            @canceledModule="fetchModuleAssemblyTasks"
            @closeDialog="cancelDialog = false"
          />
          <link-tact-dialog
            v-if="focusedTask"
            :displayed="linkTactDialog"
            :task="focusedTask"
            @closeDialog="linkTactDialog = false"
          />
          <v-dialog v-model="pingDialog" max-width="400">
            <v-card>
              <v-card-title>Send test message</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="pingMessage"
                  autofocus
                  dense
                  filled
                  hide-details
                  placeholder="Custom message"
                  @keyup.enter="
                    pingMessage.length > 0 && sendPingToAllLocations()
                  "
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="pingDialog = false">Cancel</v-btn>
                <v-btn
                  :disabled="pingMessage.length === 0"
                  color="primary"
                  elevation="0"
                  @click="sendPingToAllLocations"
                  >Send</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="recalibrateConfirmDialog" max-width="460">
            <v-card>
              <v-card-title>Recalibrate hall</v-card-title>
              <v-card-text>
                This will reset all station positions and rebuild the hall
                layout using
                <strong>{{ focusedTask && focusedTask.title }}</strong> as the
                anchor at station 1. All other positions will be recalculated.
                This action cannot be undone.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  text
                  :disabled="recalibrateHallLoading"
                  @click="recalibrateConfirmDialog = false"
                  >Cancel</v-btn
                >
                <v-btn
                  color="error"
                  elevation="0"
                  :loading="recalibrateHallLoading"
                  @click="recalibrateHall"
                  >Recalibrate</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>

        <div class="flex-1 overflow-y-auto pa-2">
          <div
            v-if="locations.length === 0 && !statusProject"
            class="font-italic"
          >
            There are no active locations
          </div>
          <div
            v-for="lane in activeLanes"
            :key="lane.id"
            class="d-flex flex-column mb-2 mt-5"
          >
            <div class="d-flex align-center">
              <v-chip
                v-if="lane"
                :color="lane.color"
                :text-color="getBlackOrWhiteBasedOnBackground(lane.color)"
                class="mr-5"
                >{{ lane.title }}
              </v-chip>
              <v-chip outlined small>
                has
                {{ locations.filter((l) => l?.lane?.id === lane.id).length }}
                Locations
              </v-chip>
            </div>

            <div
              v-for="location in locations.filter(
                (l) => l?.lane?.id === lane.id
              )"
              :key="location.id"
              class="d-flex flex-column px-4"
            >
              <div class="d-flex align-center py-2">
                <span class="fs-12 font-italic mx-2">{{ location.order }}</span>
                <span class="mr-5">{{ location.assembly_location }}</span>
                <span class="font-italic">{{ location.description }}</span>
                <v-spacer />
                <v-chip
                  v-if="
                    tasksOnLocations.filter(
                      (t) => t.task_type.custom_11 === location.id
                    ).length > 0
                  "
                  label
                  outlined
                  small
                  >{{
                    tasksOnLocations.find(
                      (t) => t.task_type.custom_11 === location.id
                    )?.task_project?.name
                  }}
                </v-chip>
                <v-menu v-if="focusedHall.automated" bottom left>
                  <template #activator="{ on, attrs }">
                    <v-icon class="ant-icon ml-2" dense v-bind="attrs" v-on="on"
                      >mdi-dots-vertical
                    </v-icon>
                  </template>
                  <v-list dense>
                    <v-list-item @click="sendManuallyLocationOk(location.id)">
                      <v-list-item-icon class="mr-2">
                        <v-icon dense> mdi-check-network</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>
                        Send location OK to mes
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div
                v-if="canPlaceFocusedTask"
                class="place-here-container d-flex align-center justify-center full-height rounded overflow-hidden cursor-pointer mb-2"
                @click="setTaskOnLocation(location.id)"
              >
                Place here
              </div>
              <dhme-station-dashboard-assembly-task-card
                v-for="task in tasksOnLocations.filter(
                  (t) =>
                    t.task_type.custom_11 === location.id &&
                    !toDoTasks.some((x) => x.id === t.id)
                )"
                :key="task.id"
                :collision-starts="collisionPlannedStarts"
                :details="true"
                :focused-task="focusedTask"
                :task="task"
                @click="focusOnTask(task)"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="d-flex flex-column overflow-y-auto full-height"
        style="width: 20%"
      >
        <v-toolbar dense flat height="50" style="max-height: 50px">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon class="mr-2" v-bind="attrs" v-on="on">
                mdi-information-box-outline
              </v-icon>
            </template>
            <span
              >Below you will the tasks which are moved out of the hall
            </span>
          </v-tooltip>
          <span> OUT HALL ({{ outHallTasks?.length ?? '-' }})</span>
          <v-spacer />
          <v-menu v-if="focusedOutHallTask && isProductieManager" bottom left>
            <template #activator="{ on, attrs }">
              <v-icon class="ant-icon ml-2" dense v-bind="attrs" v-on="on">
                mdi-dots-vertical
              </v-icon>
            </template>
            <v-list dense>
              <v-list-item
                :disabled="focusedOutHallTask.status === 'closed'"
                @click="markAsComplete"
              >
                <v-list-item-icon>
                  <v-icon>mdi-checkbox-marked-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Mark as complete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
        <div v-if="!isLoading" class="overflow-y-auto flex-1 my-2 px-2">
          <dhme-station-dashboard-assembly-task-card
            v-for="task in outHallTasks"
            :key="task.id"
            :collision-starts="collisionPlannedStarts"
            :details="true"
            :focused-task="focusedOutHallTask"
            :highlight-closed="true"
            :task="task"
            @click="focusOnTask(task, true)"
          />
        </div>
        <div v-else class="flex-1 d-flex align-center justify-center">
          <ant-loading />
        </div>
      </div>
      <div class="d-flex flex-column" style="width: 30%">
        <v-toolbar dense flat height="50" style="max-height: 50px">
          <span> Tasks ({{ assemblySubTasks?.length ?? '-' }})</span>
        </v-toolbar>
        <div
          v-if="focusedTask && subTasksLoading"
          class="flex-1 d-flex align-center justify-center"
        >
          <ant-loading />
        </div>
        <div
          v-if="focusedTask && !subTasksLoading"
          class="d-flex ant-border-left-darker full-width overflow-y-auto flex-1"
        >
          <v-dialog key="table-preview" v-model="previewDialog">
            <div class="iframe-container" style="height: 80vh">
              <v-img
                :src="imagePreview?.url"
                contain
                height="100%"
                width="100%"
              />
              <div class="iframe-actions-container">
                <v-btn color="error" plain small @click="previewDialog = false">
                  <v-icon> mdi-close</v-icon>
                  Close
                </v-btn>
              </div>
            </div>
          </v-dialog>
          <div class="d-flex flex-column flex-1 overflow-y-auto">
            <div
              v-if="assemblyLocations.length > 0"
              class="overflow-y-auto flex-1"
            >
              <div
                v-for="location in assemblyLocations"
                :key="location.id"
                class="px-2 py-4 ant-border-bottom"
              >
                <v-subheader>
                  Station {{ location.assembly_location }} -
                  {{ location.description }}
                  <v-btn
                    v-if="
                      taskToMove &&
                      taskToMove?.task_type?.custom_2 !== location.id
                    "
                    class="ml-5"
                    color="primary"
                    elevation="0"
                    small
                    @click="moveTask(location)"
                    >move here
                  </v-btn>
                  <v-spacer />
                  <v-tooltip left>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        @click="forceReloadLocation(location.id)"
                        v-on="on"
                      >
                        <v-icon>mdi-reload</v-icon>
                      </v-btn>
                    </template>
                    <span>Reload location</span>
                  </v-tooltip>
                </v-subheader>
                <v-progress-linear
                  :value="totalProgressAcrossSteps"
                  class="mb-2"
                  color="primary"
                  height="15"
                  rounded
                >
                  <strong
                    :style="{
                      color: getBlackOrWhiteBasedOnBackground(
                        $vuetify.theme.themes.light.primary
                      ),
                    }"
                    class="fs-10"
                    >{{ Math.ceil(totalProgressAcrossSteps) }}%</strong
                  >
                </v-progress-linear>
                <v-card class="mb-2 overflow-hidden" outlined>
                  <v-tabs
                    v-model="stepTab"
                    center-active
                    color="primary"
                    grow
                    show-arrows
                  >
                    <v-tab
                      v-for="step in assemblyLocationSteps"
                      :key="`assembly-location-step-${
                        step?.id ? step.id : step
                      }`"
                      >{{
                        step?.id
                          ? `${step?.order} -
                      ${step?.title}`
                          : step
                      }}
                    </v-tab>
                  </v-tabs>
                </v-card>
                <div class="d-flex">
                  <div class="flex-1">
                    <dhme-hall-dashboard-assembly-sub-task-card
                      v-for="task in getTasksByLocation(location.id)"
                      :key="`${task.id}-${task.status}`"
                      :task="task"
                      @previewImage="previewImage"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="mt-5 mx-5">
              Tasks will be automatically generated within the
              <span class="font-weight-bold">Station Dashboard App</span> or
              manually created using the
              <span class="font-weight-bold">Assembly Preparations App </span>
              within the project.
            </div>
          </div>
        </div>
        <div v-else-if="!subTasksLoading" class="mt-5 mx-5 font-italic">
          Please select a task first
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.place-here-container {
  width: 100%;
  height: 70px;
  border: dashed 2px var(--v-primary-base);
  transition: 200ms;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.iframe-container {
  position: relative;

  .iframe-actions-container {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(8px);
    border-radius: 5px;
    position: absolute;
    bottom: 10px;
    left: calc(50% - 100px);
    z-index: 1;
    padding: 12px 16px;
  }
}
</style>
